import { useFormHandler } from "shared/hooks";
import { useCallback } from "react";
import { z } from "zod";
import i18n from "i18next";
import {
  createMultilingual,
  getAddressString,
  getMultilingualString,
  phoneNumberRegex,
  updateMultilingual,
} from "shared/utils";
import { i18nNamespace, loadLanguagePacks } from "i18n";
import {
  EnumAddressLocationType,
  EnumOperatorPricing,
  EnumOperatorType,
  OperatorByIdDocument,
  OperatorByIdQuery,
  OperatorHoursOfOperation,
  OperatorNameByIdDocument,
  OperatorUpdateMutationVariables,
  SeasonalityEnum,
  useOperatorUpdateMutation,
} from "gql/generated";
import { SocialLinks } from "social-links";
import _ from "lodash";

const socialLinks = new SocialLinks();

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Directory]);

const updateProfileFormSchema = z.object({
  businessName: z
    .string()
    .min(
      1,
      `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.BUSINESS_NAME")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    ),
  businessType: z
    .string()
    .min(
      1,
      `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.BUSINESS_TYPE.TITLE")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    ),
  pricing: z
    .string()
    .min(
      1,
      `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.PROFILE.KEY_INFO.PRICING")}`,
    )
    .optional()
    .or(z.literal("")),
  website: z
    .string()
    .url(
      `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_WEBSITE")}. ${i18n.t("core:FORM_ERRORS.ADD_HTTP")}`,
    )
    .optional()
    .or(z.literal("")),
  phone: z
    .string()
    .regex(
      phoneNumberRegex,
      `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.LOCAL_PUBLIC_PHONE")}`,
    )
    .optional()
    .or(z.literal("")),
  tollFreePhone: z
    .string()
    .regex(
      phoneNumberRegex,
      `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.TOLL_FREE_PUBLIC_PHONE")}`,
    )
    .optional()
    .or(z.literal("")),
  email: z
    .string()
    .min(
      1,
      `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_EMAIL_ADDRESS")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    )
    .email(
      `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_EMAIL_ADDRESS")}`,
    )
    .optional()
    .or(z.literal("")),
  fullAddress: z.string().optional(),
  fullAddressObj: z
    .object({
      line1: z.string().optional().default(""),
      line2: z.string().optional().default(""),
      city: z.string().optional().default(""),
      subdivision: z.string().optional().default(""),
      postalCode: z.string().optional().default(""),
      country: z.string().optional().default(""),
      location: z
        .object({
          type: z.string().default("Point"),
          coordinates: z.object({
            lat: z.number().min(-90).max(90),
            lng: z.number().min(-180).max(180),
          }),
        })
        .optional(),
    })
    .partial()
    .optional(),
  longDescription: z
    .string()
    .min(
      1,
      `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.PROFILE.KEY_INFO.LONG_DESCRIPTION")}`,
    )
    .optional()
    .or(z.literal("")),
  shortDescription: z
    .string()
    .min(
      1,
      `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.PROFILE.KEY_INFO.SHORT_DESCRIPTION")}`,
    )
    .optional()
    .or(z.literal("")),
  facebook: z
    .string()
    .refine((data) => socialLinks.isValid("facebook", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.PROFILE.SOCIAL_LINKS.FACEBOOK")}`,
    })
    .optional()
    .or(z.literal("")),
  instagram: z
    .string()
    .refine((data) => socialLinks.isValid("instagram", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.PROFILE.SOCIAL_LINKS.INSTAGRAM")}`,
    })
    .optional()
    .or(z.literal("")),
  x: z.string().optional().or(z.literal("")),
  youtube: z
    .string()
    .refine((data) => socialLinks.isValid("youtube", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.PROFILE.SOCIAL_LINKS.YOUTUBE")}`,
    })
    .optional()
    .or(z.literal("")),
  pinterest: z
    .string()
    .refine((data) => socialLinks.isValid("pinterest", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.PROFILE.SOCIAL_LINKS.PINTEREST")}`,
    })
    .optional()
    .or(z.literal("")),
  googleMyBusiness: z.string().optional().or(z.literal("")),
  tiktok: z
    .string()
    .refine((data) => socialLinks.isValid("tiktok", data), {
      message: `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.PROFILE.SOCIAL_LINKS.TIKTOK")}`,
    })
    .optional()
    .or(z.literal("")),
  tripAdvisor: z.string().optional().or(z.literal("")),
  bookNow: z.string().optional().or(z.literal("")),
  keywords: z.array(z.string()),
  amenities: z.array(z.string()),
  businessTypeTags: z.array(z.string()),
  hoursOfOperation: z.object({
    sun: z.object({ start: z.string(), end: z.string() }),
    mon: z.object({ start: z.string(), end: z.string() }),
    tue: z.object({ start: z.string(), end: z.string() }),
    wed: z.object({ start: z.string(), end: z.string() }),
    thu: z.object({ start: z.string(), end: z.string() }),
    fri: z.object({ start: z.string(), end: z.string() }),
    sat: z.object({ start: z.string(), end: z.string() }),
  }),
  seasonality: z.object({
    jan: z.string().optional(),
    feb: z.string().optional(),
    mar: z.string().optional(),
    apr: z.string().optional(),
    may: z.string().optional(),
    jun: z.string().optional(),
    jul: z.string().optional(),
    aug: z.string().optional(),
    sep: z.string().optional(),
    oct: z.string().optional(),
    nov: z.string().optional(),
    dec: z.string().optional(),
  }),
});

export type UpdateProfileFormValues = z.infer<typeof updateProfileFormSchema>;

interface UpdateProfileFormOptions {
  operator?: OperatorByIdQuery["operatorById"];
  onSuccess?: (v: string) => void;
  onError?: (message: string) => void;
  isApprovingOperator?: boolean;
}

function getDefaultValues(
  operator?: OperatorByIdQuery["operatorById"],
): UpdateProfileFormValues {
  return {
    businessName: operator?.name ? getMultilingualString(operator.name) : "",
    businessType: operator?.type ? operator.type : "",
    pricing: operator?.pricing ? operator.pricing : "",
    website: operator?.contact.links?.website
      ? operator.contact.links.website
      : "",
    phone: operator?.contact.phone ? operator.contact.phone : "",
    tollFreePhone: operator?.contact.tollFreePublicPhone
      ? operator.contact.tollFreePublicPhone
      : "",
    email: operator?.contact.email ? operator.contact.email : "",
    fullAddress: operator?.contact.address
      ? getAddressString(operator.contact.address)
      : "",
    fullAddressObj: {
      line1: operator?.contact.address?.line1
        ? operator.contact.address.line1
        : "",
      line2: operator?.contact.address?.line2
        ? operator.contact.address.line2
        : "",
      city: operator?.contact.address?.city
        ? operator.contact.address.city
        : "",
      subdivision: operator?.contact.address?.subdivision
        ? operator.contact.address.subdivision
        : "",
      postalCode: operator?.contact.address?.postalCode
        ? operator.contact.address.postalCode
        : "",
      country: operator?.contact.address?.country
        ? operator.contact.address.country
        : "",
      location: {
        type: operator?.contact.address?.location?.type
          ? operator.contact.address.location?.type
          : "Point",
        coordinates: {
          lng: operator?.contact.address?.location?.coordinates[0]
            ? operator.contact.address.location.coordinates[0]
            : 0,
          lat: operator?.contact.address?.location?.coordinates[1]
            ? operator.contact.address.location.coordinates[1]
            : 0,
        },
      },
    },
    longDescription: operator?.description
      ? getMultilingualString(operator.description)
      : "",
    shortDescription: operator?.shortDescription
      ? getMultilingualString(operator.shortDescription)
      : "",
    facebook: operator?.contact?.links?.facebook
      ? operator.contact.links.facebook
      : "",
    instagram: operator?.contact?.links?.instagram
      ? operator.contact.links.instagram
      : "",
    x: operator?.contact?.links?.x ? operator.contact.links.x : "",
    youtube: operator?.contact?.links?.youtube
      ? operator.contact.links.youtube
      : "",
    pinterest: operator?.contact?.links?.pinterest
      ? operator.contact.links.pinterest
      : "",
    googleMyBusiness: operator?.contact?.links?.googleMyBusiness
      ? operator.contact.links.googleMyBusiness
      : "",
    tiktok: operator?.contact?.links?.tiktok
      ? operator.contact.links.tiktok
      : "",
    tripAdvisor: operator?.contact?.links?.tripAdvisor
      ? operator.contact.links.tripAdvisor
      : "",
    bookNow: operator?.contact?.links?.bookNow
      ? operator.contact.links.bookNow
      : "",
    keywords: operator?.tags?.global
      ? operator.tags.global.map((tag) => tag._id)
      : [],
    amenities: operator?.tags?.amenity
      ? operator.tags.amenity.map((tag) => tag._id)
      : [],
    businessTypeTags: operator?.tags?.businessType
      ? operator.tags.businessType.map((tag) => tag._id)
      : [],
    hoursOfOperation: {
      sun: {
        start: operator?.hoursOfOperation?.sun
          ? operator.hoursOfOperation.sun.start
          : "",
        end: operator?.hoursOfOperation?.sun
          ? operator.hoursOfOperation.sun.end
          : "",
      },
      mon: {
        start: operator?.hoursOfOperation?.mon
          ? operator?.hoursOfOperation.mon.start
          : "",
        end: operator?.hoursOfOperation?.mon
          ? operator?.hoursOfOperation.mon.end
          : "",
      },
      tue: {
        start: operator?.hoursOfOperation?.tue
          ? operator.hoursOfOperation.tue.start
          : "",
        end: operator?.hoursOfOperation?.tue
          ? operator.hoursOfOperation.tue.end
          : "",
      },
      wed: {
        start: operator?.hoursOfOperation?.wed
          ? operator.hoursOfOperation.wed.start
          : "",
        end: operator?.hoursOfOperation?.wed
          ? operator.hoursOfOperation.wed.end
          : "",
      },
      thu: {
        start: operator?.hoursOfOperation?.thu
          ? operator.hoursOfOperation.thu.start
          : "",
        end: operator?.hoursOfOperation?.thu
          ? operator.hoursOfOperation.thu.end
          : "",
      },
      fri: {
        start: operator?.hoursOfOperation?.fri
          ? operator.hoursOfOperation.fri.start
          : "",
        end: operator?.hoursOfOperation?.fri
          ? operator.hoursOfOperation.fri.end
          : "",
      },
      sat: {
        start: operator?.hoursOfOperation?.sat
          ? operator.hoursOfOperation.sat.start
          : "",
        end: operator?.hoursOfOperation?.sat
          ? operator.hoursOfOperation.sat.end
          : "",
      },
    },
    seasonality: {
      jan: operator?.seasonality?.jan ? operator.seasonality.jan : undefined,
      feb: operator?.seasonality?.feb ? operator.seasonality.feb : undefined,
      mar: operator?.seasonality?.mar ? operator.seasonality.mar : undefined,
      apr: operator?.seasonality?.apr ? operator.seasonality.apr : undefined,
      may: operator?.seasonality?.may ? operator.seasonality.may : undefined,
      jun: operator?.seasonality?.jun ? operator.seasonality.jun : undefined,
      jul: operator?.seasonality?.jul ? operator.seasonality.jul : undefined,
      aug: operator?.seasonality?.aug ? operator.seasonality.aug : undefined,
      sep: operator?.seasonality?.sep ? operator.seasonality.sep : undefined,
      oct: operator?.seasonality?.oct ? operator.seasonality.oct : undefined,
      nov: operator?.seasonality?.nov ? operator.seasonality.nov : undefined,
      dec: operator?.seasonality?.dec ? operator.seasonality.dec : undefined,
    },
  };
}

type FormHoursOfOperationDay =
  | { start?: string | undefined; end?: string | undefined }
  | undefined;

type FormHoursOfOperation = Record<
  keyof Omit<OperatorHoursOfOperation, "__typename">,
  FormHoursOfOperationDay
>;

export const useUpdateProfileForm = ({
  operator,
  onSuccess,
  onError,
  isApprovingOperator,
}: UpdateProfileFormOptions) => {
  const defaultValues = getDefaultValues(operator);
  const [updateOperator] = useOperatorUpdateMutation({
    refetchQueries: [OperatorByIdDocument, OperatorNameByIdDocument],
    awaitRefetchQueries: true,
  });

  /**
   * Return undefined if the hours of operation day is unfilled with empty
   * strings
   */
  const getHoursOfOperationDay = (
    hoursOfOperationDay: FormHoursOfOperationDay,
  ) => {
    return hoursOfOperationDay &&
      hoursOfOperationDay.start &&
      hoursOfOperationDay.end &&
      !(hoursOfOperationDay.start === "" && hoursOfOperationDay.end === "")
      ? {
          start: hoursOfOperationDay.start,
          end: hoursOfOperationDay.end,
        }
      : undefined;
  };

  /**
   * Normalize the hours of op data by returning undefined for any unfilled
   * start/end objects per day of the week
   */
  const normalizeHoursOfOperation = useCallback(
    (hoursOfOperation: FormHoursOfOperation) => {
      for (const day in hoursOfOperation) {
        const dayKey = day as keyof FormHoursOfOperation;

        hoursOfOperation[dayKey] = getHoursOfOperationDay(
          hoursOfOperation[dayKey],
        );
      }

      return hoursOfOperation;
    },
    [],
  );
  return useFormHandler<UpdateProfileFormValues>(
    useCallback(
      async (data) => {
        const formErrorMessage = i18n.t(
          "directory:OPERATOR.ERROR.UPDATING_OPERATOR",
        );
        if (data) {
          const newHoursOfOperation = data.hoursOfOperation
            ? normalizeHoursOfOperation(
                data.hoursOfOperation as FormHoursOfOperation,
              )
            : undefined;
          const defaultHoursOfOperation = normalizeHoursOfOperation(
            defaultValues.hoursOfOperation,
          );
          const hasHoursOfOperationChanged = !_.isEqual(
            newHoursOfOperation,
            defaultHoursOfOperation,
          );

          // If the fullAddressObj exists & line1 has a length greater than 0 -
          // based on the zod validation the rest of the address should be
          // filled out & will be accessible to use in the payload.
          const address =
            data.fullAddressObj &&
            data.fullAddressObj.line1 &&
            data.fullAddressObj.line1?.length > 0
              ? {
                  line1: data.fullAddressObj.line1 ?? "",
                  line2: data.fullAddressObj.line2 ?? "",
                  city: data.fullAddressObj.city ?? "",
                  subdivision: data.fullAddressObj.subdivision ?? "",
                  postalCode: data.fullAddressObj.postalCode ?? "",
                  country: data.fullAddressObj.country ?? "",
                  location: {
                    type:
                      (data.fullAddressObj.location
                        ?.type as EnumAddressLocationType) ?? "",
                    coordinates: [
                      data.fullAddressObj?.location?.coordinates?.lng ?? 0,
                      data.fullAddressObj?.location?.coordinates?.lat ?? 0,
                    ],
                  },
                }
              : undefined;

          const operatorPayload: OperatorUpdateMutationVariables = {
            entityId: operator?._entityId,
            isApproving: isApprovingOperator,
            record: {
              name: data.businessName
                ? operator?.name
                  ? updateMultilingual(operator.name, data.businessName)
                  : createMultilingual(data.businessName)
                : undefined,
              type: (data.businessType as EnumOperatorType) ?? "",
              pricing:
                data.pricing && data.pricing.length > 0
                  ? (data.pricing as EnumOperatorPricing)
                  : undefined,
              contact: {
                email:
                  data.email && data.email.length > 0 ? data.email : undefined,
                phone:
                  data.phone && data.phone.length > 0 ? data.phone : undefined,
                tollFreePublicPhone:
                  data.tollFreePhone && data.tollFreePhone.length > 0
                    ? data.tollFreePhone
                    : undefined,
                address,
                links: {
                  website:
                    data.website && data.website.length > 0
                      ? data.website
                      : undefined,
                  facebook:
                    data.facebook && data.facebook.length > 0
                      ? data.facebook
                      : undefined,
                  instagram:
                    data.instagram && data.instagram.length > 0
                      ? data.instagram
                      : undefined,
                  youtube:
                    data.youtube && data.youtube.length > 0
                      ? data.youtube
                      : undefined,
                  pinterest:
                    data.pinterest && data.pinterest.length > 0
                      ? data.pinterest
                      : undefined,
                  googleMyBusiness:
                    data.googleMyBusiness && data.googleMyBusiness.length > 0
                      ? data.googleMyBusiness
                      : undefined,
                  tiktok:
                    data.tiktok && data.tiktok.length > 0
                      ? data.tiktok
                      : undefined,
                  tripAdvisor:
                    data.tripAdvisor && data.tripAdvisor.length > 0
                      ? data.tripAdvisor
                      : undefined,
                  bookNow:
                    data.bookNow && data.bookNow.length > 0
                      ? data.bookNow
                      : undefined,
                },
              },
              shortDescription: data.shortDescription
                ? operator?.shortDescription
                  ? updateMultilingual(
                      operator.shortDescription,
                      data.shortDescription,
                    )
                  : createMultilingual(data.shortDescription)
                : undefined,
              description: data.longDescription
                ? operator?.description
                  ? updateMultilingual(
                      operator.description,
                      data.longDescription,
                    )
                  : createMultilingual(data.longDescription)
                : undefined,
              tags: {
                organizationSpecific: operator?.tags?.organizationSpecific.map(
                  (tag) => tag._id,
                ),
                businessType: data.businessTypeTags,
                global: data.keywords,
                amenity: data.amenities,
              },
              hoursOfOperation: hasHoursOfOperationChanged
                ? (newHoursOfOperation as OperatorUpdateMutationVariables["record"]["hoursOfOperation"])
                : undefined,
              seasonality: {
                jan: (data.seasonality?.jan as SeasonalityEnum) ?? undefined,
                feb: (data.seasonality?.feb as SeasonalityEnum) ?? undefined,
                mar: (data.seasonality?.mar as SeasonalityEnum) ?? undefined,
                apr: (data.seasonality?.apr as SeasonalityEnum) ?? undefined,
                may: (data.seasonality?.may as SeasonalityEnum) ?? undefined,
                jun: (data.seasonality?.jun as SeasonalityEnum) ?? undefined,
                jul: (data.seasonality?.jul as SeasonalityEnum) ?? undefined,
                aug: (data.seasonality?.aug as SeasonalityEnum) ?? undefined,
                sep: (data.seasonality?.sep as SeasonalityEnum) ?? undefined,
                oct: (data.seasonality?.oct as SeasonalityEnum) ?? undefined,
                nov: (data.seasonality?.nov as SeasonalityEnum) ?? undefined,
                dec: (data.seasonality?.dec as SeasonalityEnum) ?? undefined,
              },
            },
          };

          await updateOperator({
            variables: operatorPayload,
          })
            .then(async (res) => {
              if (res.data && res.data?.operatorUpdate?.name) {
                const operatorName = getMultilingualString(
                  res.data.operatorUpdate.name,
                );
                onSuccess?.(operatorName);
              }
            })
            .catch(() => {
              onError?.(formErrorMessage);
            });
        }
      },
      [
        defaultValues.hoursOfOperation,
        isApprovingOperator,
        normalizeHoursOfOperation,
        onError,
        onSuccess,
        operator?._entityId,
        operator?.description,
        operator?.name,
        operator?.shortDescription,
        operator?.tags?.organizationSpecific,
        updateOperator,
      ],
    ),
    defaultValues,
    { schema: updateProfileFormSchema },
  );
};
